export type ClipPreviewAspectRatio = "16:9" | "9:16" | "1:1" | "4:5" | "4:3";

export interface ClipPreviewEditorState {
  zoom: number;
  offsetX: number;
  offsetY: number;
}

export interface VideoSnapshotClipPreviewModel {
  version: 1;
  sourceType: "video-snapshot";
  previewBase64: string;
  aspectRatio: ClipPreviewAspectRatio;
  source: {
    mediaId: number | null;
    timeMs: number;
    fullEditMs: number | null;
  };
  editor: ClipPreviewEditorState;
}

export interface UploadedImageClipPreviewModel {
  version: 1;
  sourceType: "uploaded-image";
  previewBase64: string;
  uploadedImage: string;
  aspectRatio: ClipPreviewAspectRatio;
  editor: ClipPreviewEditorState;
}

export type ClipPreviewModel =
  | VideoSnapshotClipPreviewModel
  | UploadedImageClipPreviewModel;

export function isDataUrl(value: string | null | undefined): value is string {
  return typeof value === "string" && value.startsWith("data:image/");
}

export function createDefaultClipPreviewEditorState(): ClipPreviewEditorState {
  return {
    zoom: 100,
    offsetX: 0,
    offsetY: 0,
  };
}

/** Parses clip preview from a JSON string or a persisted object value. */
export function parseClipPreviewValue(value: unknown): ClipPreviewModel | null {
  if (value == null) return null;
  if (typeof value === "string") {
    return parseClipPreviewModel(value);
  }
  if (typeof value === "object") {
    try {
      return parseClipPreviewModel(JSON.stringify(value));
    } catch {
      return null;
    }
  }
  return null;
}

export function parseClipPreviewModel(value: string | null | undefined): ClipPreviewModel | null {
  if (!value?.trim()) return null;
  const trimmed = value.trim();

  try {
    const parsed = JSON.parse(trimmed) as Partial<ClipPreviewModel>;
    if (!parsed || typeof parsed !== "object") return null;
    if (
      parsed.version === 1 &&
      parsed.sourceType === "video-snapshot" &&
      isDataUrl(parsed.previewBase64) &&
      parsed.source != null &&
      typeof parsed.source === "object" &&
      typeof parsed.source.timeMs === "number"
    ) {
      return {
        version: 1,
        sourceType: "video-snapshot",
        previewBase64: parsed.previewBase64,
        aspectRatio: normalizeClipPreviewAspectRatio(parsed.aspectRatio),
        source: {
          mediaId: typeof parsed.source.mediaId === "number" ? parsed.source.mediaId : null,
          timeMs: parsed.source.timeMs,
          fullEditMs:
            typeof parsed.source.fullEditMs === "number" ? parsed.source.fullEditMs : null,
        },
        editor: normalizeClipPreviewEditorState(parsed.editor),
      };
    }

    if (
      parsed.version === 1 &&
      parsed.sourceType === "uploaded-image" &&
      isDataUrl(parsed.previewBase64) &&
      isDataUrl(parsed.uploadedImage)
    ) {
      return {
        version: 1,
        sourceType: "uploaded-image",
        previewBase64: parsed.previewBase64,
        uploadedImage: parsed.uploadedImage,
        aspectRatio: normalizeClipPreviewAspectRatio(parsed.aspectRatio),
        editor: normalizeClipPreviewEditorState(parsed.editor),
      };
    }
  } catch {
    return null;
  }

  return null;
}

export function serializeClipPreviewModel(model: ClipPreviewModel): string {
  return JSON.stringify(model);
}

/** Persisted when no clip preview model is set (matches backend EmptyClipPreviewJson). */
export const EMPTY_CLIP_PREVIEW_JSON = "{}";

/** API/DB payload for project.clip_preview (JSON column — never use empty string). */
export function serializeClipPreviewForProjectSave(
  value: string | null | undefined,
): string {
  const trimmed = value?.trim();
  return trimmed ? trimmed : EMPTY_CLIP_PREVIEW_JSON;
}

export function getClipPreviewDisplayUrl(value: string | null | undefined): string | null {
  return parseClipPreviewModel(value)?.previewBase64 ?? null;
}

/** True when persisted clip_preview contains a valid ClipPreviewModel (not `{}` or empty). */
export function hasClipPreviewModel(value: string | null | undefined): boolean {
  return parseClipPreviewValue(value) != null;
}

/** Fetches an HTTP(S) image URL and returns a data URL, or null on failure. */
export async function fetchHttpImageAsDataUrl(url: string): Promise<string | null> {
  const trimmed = url?.trim();
  if (!trimmed || !/^https?:\/\//i.test(trimmed)) {
    return null;
  }
  try {
    const response = await fetch(trimmed);
    if (!response.ok) return null;
    const blob = await response.blob();
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(new Error("read failed"));
      reader.readAsDataURL(blob);
    });
    return dataUrl.startsWith("data:image/") ? dataUrl : null;
  } catch {
    return null;
  }
}

export function createVideoSnapshotClipPreviewModel(args: {
  previewBase64: string;
  mediaId: number | null;
  timeMs: number;
  fullEditMs?: number | null;
  aspectRatio: ClipPreviewAspectRatio;
  editor?: ClipPreviewEditorState;
}): VideoSnapshotClipPreviewModel {
  return {
    version: 1,
    sourceType: "video-snapshot",
    previewBase64: args.previewBase64,
    aspectRatio: args.aspectRatio,
    source: {
      mediaId: args.mediaId,
      timeMs: args.timeMs,
      fullEditMs: args.fullEditMs ?? null,
    },
    editor: args.editor ?? createDefaultClipPreviewEditorState(),
  };
}

export function createUploadedImageClipPreviewModel(args: {
  previewBase64: string;
  uploadedImage: string;
  aspectRatio: ClipPreviewAspectRatio;
  editor?: ClipPreviewEditorState;
}): UploadedImageClipPreviewModel {
  return {
    version: 1,
    sourceType: "uploaded-image",
    previewBase64: args.previewBase64,
    uploadedImage: args.uploadedImage,
    aspectRatio: args.aspectRatio,
    editor: args.editor ?? createDefaultClipPreviewEditorState(),
  };
}

export interface ReconciledClipPreviewForEditor {
  /** Model to edit (may have updated aspectRatio / editor when reconciled). */
  model: ClipPreviewModel | null;
  editorActiveRatio: ClipPreviewAspectRatio;
  editor: ClipPreviewEditorState;
  /** When true, editor zoom/offset were reset because aspect ratio changed. */
  aspectRatioReconciled: boolean;
}

/** Maps project AspectRatio enum string to clip preview aspect ratio (4:5 when needed). */
export function toClipPreviewAspectRatio(value: string): ClipPreviewAspectRatio {
  return normalizeClipPreviewAspectRatio(value);
}

/**
 * Prepares a clip preview model for the editor when the destination config aspect ratio
 * may differ from the stored model ratio. On mismatch: keep source (timeMs / uploaded image),
 * reset editor transforms, update aspect ratio.
 */
export function reconcileClipPreviewForAspectRatio(
  model: ClipPreviewModel | null,
  targetAspectRatio: ClipPreviewAspectRatio,
): ReconciledClipPreviewForEditor {
  if (!model) {
    return {
      model: null,
      editorActiveRatio: targetAspectRatio,
      editor: createDefaultClipPreviewEditorState(),
      aspectRatioReconciled: false,
    };
  }

  if (model.aspectRatio === targetAspectRatio) {
    return {
      model,
      editorActiveRatio: targetAspectRatio,
      editor: model.editor,
      aspectRatioReconciled: false,
    };
  }

  const editor = createDefaultClipPreviewEditorState();
  if (model.sourceType === "uploaded-image") {
    return {
      model: {
        ...model,
        aspectRatio: targetAspectRatio,
        editor,
        previewBase64: model.uploadedImage,
      },
      editorActiveRatio: targetAspectRatio,
      editor,
      aspectRatioReconciled: true,
    };
  }

  return {
    model: {
      ...model,
      aspectRatio: targetAspectRatio,
      editor,
    },
    editorActiveRatio: targetAspectRatio,
    editor,
    aspectRatioReconciled: true,
  };
}

/** Returns the best URL/data URL to load into the editor (prefer source over cropped preview). */
export function getClipPreviewEditorSourceFromModel(
  model: ClipPreviewModel | null,
): string | null {
  if (!model) return null;
  if (model.sourceType === "uploaded-image") {
    return model.uploadedImage;
  }
  return model.previewBase64;
}

export function updateClipPreviewAfterEditorApply(args: {
  current: string | null | undefined;
  previewBase64: string;
  aspectRatio: ClipPreviewAspectRatio;
  editor: ClipPreviewEditorState;
  uploadedImage?: string | null;
}): ClipPreviewModel {
  const currentModel = parseClipPreviewModel(args.current);
  if (args.uploadedImage) {
    return createUploadedImageClipPreviewModel({
      previewBase64: args.previewBase64,
      uploadedImage: args.uploadedImage,
      aspectRatio: args.aspectRatio,
      editor: args.editor,
    });
  }

  if (currentModel?.sourceType === "video-snapshot") {
    return {
      ...currentModel,
      previewBase64: args.previewBase64,
      aspectRatio: args.aspectRatio,
      editor: args.editor,
    };
  }

  if (currentModel?.sourceType === "uploaded-image") {
    return {
      ...currentModel,
      previewBase64: args.previewBase64,
      aspectRatio: args.aspectRatio,
      editor: args.editor,
    };
  }

  return createUploadedImageClipPreviewModel({
    previewBase64: args.previewBase64,
    uploadedImage: args.previewBase64,
    aspectRatio: args.aspectRatio,
    editor: args.editor,
  });
}

function normalizeClipPreviewEditorState(value: unknown): ClipPreviewEditorState {
  if (!value || typeof value !== "object") return createDefaultClipPreviewEditorState();
  const editor = value as Partial<ClipPreviewEditorState>;
  return {
    zoom: typeof editor.zoom === "number" ? editor.zoom : 100,
    offsetX: typeof editor.offsetX === "number" ? editor.offsetX : 0,
    offsetY: typeof editor.offsetY === "number" ? editor.offsetY : 0,
  };
}

function normalizeClipPreviewAspectRatio(value: unknown): ClipPreviewAspectRatio {
  return value === "9:16" || value === "1:1" || value === "4:5" || value === "4:3"
    ? value
    : "16:9";
}
